/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  Grid,
  Box,
  Container,
  useTheme,
  useMediaQuery,
  Button,
  Typography,
  Modal,
  FormHelperText,
  FormControlLabel,
} from "@mui/material";
import { RootState, useAppDispatch } from "@store/store";
import { useValidatePhoneCodeMutation, useRequestPhoneCodeMutation } from "@api/onboarding";
import { ProgressBar } from "@components/ProgressBar";
import { formatTime, formatPhoneNumber, getRemainingTime } from "@helpers/funciones";
import "@styles/stylesRegistro.css";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { InactivityHandler } from "@components/InactivityHandler";
import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import * as Sentry from "@sentry/react";
import ModalBuroNipPM from "@components/ModalBuroNipPM";
import { CustomSwitch } from "@components/CustomSwitch";
import { CustomTextCiecBuro } from "@components/CustomTextCiecBuro";
import { setBuroNipPM, setBuroNipPMLastCodeRequest } from "@store/slices/registerSlice";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import useTofuSteps from "@hooks/useTofuSteps";
import { PayloadRequestPhoneCode } from "@interfaces/request";
import dayjs from "dayjs";
import { PhoneCodeInput } from "@components/PhoneCodeInput";
import useInterval from "@hooks/useInterval";

const TIME_LIMIT = 120;
const SCREEN_STATUS = "CSABE";

type FormValues = {
  code: string;
  switch: boolean;
};

const ConsultaBuroNipPM = () => {
  // hooks
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const interfaceConfig = useInterfaceConfig();
  const dispatch = useAppDispatch();
  const { numberOfSteps, currentStep } = useTofuSteps(SCREEN_STATUS);

  // redux hooks
  const buroNip = useSelector((state: RootState) => state.register.buroNipPM);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  // api hooks
  const [validatePhoneCode] = useValidatePhoneCodeMutation();
  const [requestPhoneCode, { isLoading: isRequestingCode }] = useRequestPhoneCodeMutation();

  // state
  const [secondsRemaining, setSecondsRemaining] = useState(TIME_LIMIT);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [openModalConfirm, setOpenModalConfirm] = useState(false);

  const loanInterval = useInterval(() => {
    if (secondsRemaining > 0) {
      setSecondsRemaining(secondsRemaining - 1);
    } else {
      loanInterval.setIsActiveInterval(false);
    }
  }, 1);

  const formik = useFormik<FormValues>({
    initialValues: {
      code: "",
      switch: buroNip?.termsAccepted || false,
    },
    validationSchema: yup.object().shape({
      code: yup.string(),
      switch: yup.boolean().oneOf([true], "Debes aceptar los términos y condiciones"),
    }),
    onSubmit: async () => {
      dispatch(
        setBuroNipPM({
          code: buroNip?.code,
          isValid: buroNip.isValid,
          termsAccepted: formik.values.switch,
        }),
      );
      setOpenModalConfirm(true);
    },
  });

  const validateCode = async (value: string) => {
    try {
      if (!accountUser?.code_phone || !accountUser?.phone) {
        setErrorMessage("No se encontró el número de teléfono");
        return;
      }

      formik.setFieldValue("code", value);

      if (buroNip?.isValid) {
        setErrorMessage(null);
        return;
      }

      if (value.length < 4) {
        return;
      }

      setIsLoading(true);
      setErrorMessage(null);

      await validatePhoneCode({
        country_code: accountUser.code_phone,
        phone_number: accountUser.phone,
        code: value,
      }).unwrap();

      dispatch(
        setBuroNipPM({
          ...buroNip,
          code: value,
          isValid: true,
          termsAccepted: false,
        }),
      );
    } catch (error) {
      const api_error = error as ApiError;
      if (api_error?.status === 404 || api_error?.status === 400) {
        setErrorMessage("Código incorrecto, intenta de nuevo");
        formik.setFieldValue("code", "");
        return;
      }

      dispatch(setModalErrorVisible({ open: true, error: api_error }));

      Sentry.captureException(api_error, {
        extra: {
          component: "ConsultaBuroNipPM",
          values: formik.values,
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resendCode = useCallback(async () => {
    try {
      setIsLoading(true);

      if (buroNip?.isValid || isRequestingCode) {
        return;
      }

      if (isRequestingCode || !accountUser?.code_phone || !accountUser?.phone) {
        return;
      }

      const secondsToWait = getRemainingTime(buroNip.last_code_request || "", TIME_LIMIT);

      if (secondsToWait > 0) {
        setSecondsRemaining(secondsToWait);
        loanInterval.setIsActiveInterval(true);
        return;
      }

      const currentTime = dayjs();

      dispatch(setBuroNipPMLastCodeRequest({ last_code_request: currentTime.toISOString() }));

      const payloadRequestPhoneCode: PayloadRequestPhoneCode = {
        country_code: accountUser.code_phone,
        phone_number: accountUser.phone,
      };

      try {
        await requestPhoneCode(payloadRequestPhoneCode).unwrap();
      } catch (requestErr) {
        const reqError = requestErr as ApiError;
        if (reqError?.status !== 429) {
          dispatch(setBuroNipPMLastCodeRequest({ last_code_request: null }));
        }
        throw requestErr;
      }
    } catch (error) {
      const api_error = error as ApiError;
      if (api_error?.status === 429) {
        handleOpen();
        return;
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    buroNip.isValid,
    buroNip.last_code_request,
    isRequestingCode,
    accountUser?.code_phone,
    accountUser?.phone,
    dispatch,
    requestPhoneCode,
  ]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  useEffect(() => {
    resendCode();

    if (buroNip?.isValid) {
      formik.setFieldValue("code", buroNip.code);
    }
  }, [buroNip?.isValid]);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <InactivityHandler />
        <Box
          sx={{
            mt: 2,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant={isMobileOrTablet ? "h4" : "h3"}
            fontWeight={500}
            textAlign="center"
            color={"primary.dark"}
          >
            {interfaceConfig.textos_tofu?.pm?.consulta_buro_nip?.titulo || "Validación para consulta buró"}
          </Typography>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
            <ProgressBar totalSteps={numberOfSteps} currentStep={currentStep} />
          </Container>

          <Box maxWidth={"sm"} mt={isMobileOrTablet ? 2 : 3} textAlign={"center"}>
            <Typography variant={isMobileOrTablet ? "body2" : "body1"} color="primary.dark">
              Ingresa el código que enviamos vía whatsapp al número
            </Typography>
          </Box>

          <Typography
            variant={isMobileOrTablet ? "h5" : "h4"}
            mt={isMobileOrTablet ? 2 : 3}
            fontWeight={600}
            color={"primary.main"}
          >
            ({accountUser?.code_phone}) {formatPhoneNumber(accountUser?.phone || "")}
          </Typography>

          {!buroNip?.isValid && (
            <Typography
              mt={1}
              variant={isMobileOrTablet ? "body2" : "body2"}
              component={"a"}
              href="/producto/creditosimple/registro_datos_personales"
              sx={{
                cursor: "pointer",
                textDecoration: "underline",
              }}
              color="primary.main"
            >
              ¿No es tu número? Editalo aquí
            </Typography>
          )}

          <Typography mt={2} variant={isMobileOrTablet ? "body2" : "body1"} fontWeight={300} color="primary.dark">
            Código {buroNip?.isValid ? "verificado" : "no verificado"}
          </Typography>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 1 }}>
            <form onSubmit={formik.handleSubmit} noValidate>
              <PhoneCodeInput
                name="code"
                value={formik.values.code}
                onBlur={formik.handleBlur}
                disabled={isLoading || formik.isSubmitting || buroNip?.isValid}
                onComplete={validateCode}
              />

              {errorMessage && <FormHelperText sx={{ mt: isMobileOrTablet ? 2 : 1 }}>{errorMessage}</FormHelperText>}

              {!buroNip?.isValid && (
                <>
                  <Typography
                    variant={isMobileOrTablet ? "body2" : "body1"}
                    mt={isMobileOrTablet ? 2 : 3}
                    textAlign="center"
                    fontWeight={600}
                    color="primary.main"
                  >
                    {formatTime(secondsRemaining)} min
                  </Typography>

                  {secondsRemaining === 0 && (
                    <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3}>
                      <Typography
                        variant={isMobileOrTablet ? "body2" : "body1"}
                        component={"a"}
                        onClick={resendCode}
                        sx={{ cursor: "pointer", textDecoration: "underline" }}
                        color="primary.dark"
                      >
                        Reenviar código
                      </Typography>
                    </Box>
                  )}
                </>
              )}

              <Box display={"flex"} justifyContent={"center"} mt={2} textAlign="center">
                <FormControlLabel
                  control={<CustomSwitch />}
                  label={
                    <Box sx={{ paddingTop: "10px" }}>
                      <Typography variant="body2" color="primary.dark">
                        <CustomTextCiecBuro type="nipBuro" />
                      </Typography>
                    </Box>
                  }
                  name="switch"
                  checked={formik.values.switch}
                  onChange={formik.handleChange}
                  disabled={isLoading || formik.isSubmitting || !buroNip?.isValid}
                />
              </Box>

              <Box display="flex" justifyContent="center" mt={2}>
                <Button
                  variant="default"
                  type="submit"
                  color="primary"
                  disabled={isLoading || !buroNip?.isValid || formik.isSubmitting || !formik.isValid}
                >
                  Continuar
                </Button>
              </Box>
            </form>
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}

      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Typography fontWeight={"600"} gutterBottom color="primary.dark" fontSize={"20px"}>
            Excedió el número de intentos permitidos para ese número
          </Typography>
          <Typography fontSize={"16px"} sx={{ mt: 2, mb: 2 }} color="primary.dark">
            intenta más tarde o contacta a un asesor.
          </Typography>
          <Button variant="default" color="primary" onClick={handleClose}>
            Cerrar
          </Button>
        </Box>
      </Modal>
      <ModalBuroNipPM screenStatus={SCREEN_STATUS} open={openModalConfirm} onClose={() => setOpenModalConfirm(false)} />
    </Grid>
  );
};

export default ConsultaBuroNipPM;
